import { useCallback, useEffect, useRef, useState } from 'react';
import { STATUS_TEMPLATES } from './statusTemplates';
import { readStatus } from './statusReader';
import { createStatusTracker, formatStatusEntry } from './statusLog';
import { GAME_TIME_TEMPLATES } from '../gameTime/gameTimeTemplates';
import { formatGameTime, readGameTime } from '../gameTime/gameTimeReader';
import { createClockTracker } from '../gameTime/clockTracker';
import { buffEndGameSec } from '../gameEvents/wickelineTracker';

const SAMPLE_INTERVAL_MS = 1500; // 게임 화면에서 읽는 간격

/**
 * 게임 중(enabled)에 공유 화면 우측 상단의 "남은 팀 수 / 인원"을 주기적으로 읽어, 값이 바뀔 때마다 로그를 남긴다.
 * - captureStatusImage(): 패널 영역 이미지(420×120). 공유 중이 아니면 null.
 * - captureGameTimeImage(): 좌측 하단 게임 시계 영역 이미지(300×60). 기록하는 순간의 게임 시각을 로그에 적는다.
 *   시계 숫자는 가끔 못 읽히거나 틀리게 읽혀서 추적기(clockTracker)로 이어서 센 값을 쓴다. 읽지 못했으면 게임 시각은 null.
 * - phaseLabel: 그 시점의 "2일차 낮" 같은 글(없으면 null). 로그 항목에 같이 적는다.
 * - resetToken: 값이 바뀌면(새 게임 시작) 로그를 비운다. 게임이 끝나거나 공유가 끊겨도 로그는 다음 게임 시작 전까지 남긴다.
 * 항목: { id, at(기록한 실제 시각 Date), gameTime('MM:SS' | null), phaseLabel, change, text }. "몇 분 몇 초 전"은 화면에 그릴 때 at과 지금의 실제 시각을 비교해 만든다.
 * 위클라인 처치 감지는 useWickelineWatch가 하고(0.5초 주기), 처치가 알려지면 recordWickelineKill(nowMs)로 로그 한 줄과 버프 상태를 남긴다.
 * 반환: { entries(오래된 순), summary: { teams, players, playersHidden }, wickelineKill: { at(감지한 실제 시각 Date), gameSec(그때의 게임 시각 초 | null) } | null, recordWickelineKill }
 */
// 위클라인 처치 로그 글. 게임 시각을 알면 버프 종료 게임 시각을 함께 적는다.
const wickelineText = (gameSec) => {
  const end = formatGameTime(buffEndGameSec(gameSec));
  return end ? `위클라인 처치 — 위클 버프 ${end}에 종료` : '위클라인 처치 — 위클 버프 3분 30초';
};

export default function useStatusLog({ enabled, captureStatusImage, captureGameTimeImage, phaseLabel, resetToken }) {
  const trackerRef = useRef(null);
  if (!trackerRef.current) {
    trackerRef.current = createStatusTracker();
  }
  const clockTrackerRef = useRef(null);
  if (!clockTrackerRef.current) {
    clockTrackerRef.current = createClockTracker();
  }
  const captureRef = useRef(captureStatusImage);
  captureRef.current = captureStatusImage;
  const captureTimeRef = useRef(captureGameTimeImage);
  captureTimeRef.current = captureGameTimeImage;
  const phaseRef = useRef(phaseLabel);
  phaseRef.current = phaseLabel;
  const nextIdRef = useRef(1);
  const missingGameTimeRef = useRef(false); // 게임 시각을 아직 못 채운 항목이 있는지(시계 추적기가 자리 잡기 전에 기록된 경우)
  const [entries, setEntries] = useState([]);
  const [wickelineKill, setWickelineKill] = useState(null);

  // 새 게임이 시작되면 로그와 추적 상태를 비운다
  useEffect(() => {
    trackerRef.current.reset();
    clockTrackerRef.current.reset();
    missingGameTimeRef.current = false;
    setEntries([]);
    setWickelineKill(null);
  }, [resetToken]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const sample = () => {
      const image = captureRef.current && captureRef.current();
      if (!image) {
        return;
      }
      const now = Date.now();
      // 게임 시계를 읽어 추적기를 갱신한다(못 읽어도 직전 값에서 이어서 센다)
      const timeImage = captureTimeRef.current && captureTimeRef.current();
      if (timeImage) {
        clockTrackerRef.current.update(readGameTime(timeImage, GAME_TIME_TEMPLATES), now);
      }
      const changes = trackerRef.current.update(readStatus(image, STATUS_TEMPLATES));
      if (changes.length) {
        const gameTime = formatGameTime(clockTrackerRef.current.elapsedAt(now));
        const at = new Date(now);
        const added = changes.map((change) => {
          const id = nextIdRef.current;
          nextIdRef.current += 1;
          return { id, at, gameTime, phaseLabel: phaseRef.current || null, change, text: formatStatusEntry(change) };
        });
        if (gameTime === null) {
          missingGameTimeRef.current = true;
        }
        setEntries((prev) => [...prev, ...added]);
      }
      // 시계 추적기가 자리 잡은 뒤에는 앞서 비어 있던 게임 시각을 그 기록 시각으로 거슬러 계산해 채운다
      if (missingGameTimeRef.current && clockTrackerRef.current.elapsedAt(now) !== null) {
        missingGameTimeRef.current = false;
        setEntries((prev) => prev.map((entry) => {
          if (entry.gameTime !== null) {
            return entry;
          }
          const gameSec = clockTrackerRef.current.elapsedAt(entry.at.getTime());
          return {
            ...entry,
            gameTime: formatGameTime(gameSec),
            text: entry.change.kind === 'wickeline' ? wickelineText(gameSec) : entry.text,
          };
        }));
        setWickelineKill((prev) => (prev && prev.gameSec === null
          ? { ...prev, gameSec: clockTrackerRef.current.elapsedAt(prev.at.getTime()) }
          : prev));
      }
    };
    sample();
    const timer = setInterval(sample, SAMPLE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled]);

  // 위클라인 처치가 감지되면(useWickelineWatch가 부름) 그 순간의 게임 시각으로 로그 한 줄과 버프 상태를 남긴다
  const recordWickelineKill = useCallback((nowMs) => {
    const gameSec = clockTrackerRef.current.elapsedAt(nowMs);
    const id = nextIdRef.current;
    nextIdRef.current += 1;
    const entry = {
      id, at: new Date(nowMs), gameTime: formatGameTime(gameSec), phaseLabel: phaseRef.current || null,
      change: { kind: 'wickeline' }, text: wickelineText(gameSec),
    };
    if (entry.gameTime === null) {
      missingGameTimeRef.current = true;
    }
    setEntries((prev) => [...prev, entry]);
    setWickelineKill({ at: entry.at, gameSec });
  }, []);

  // 지금(마지막으로 확정된) 남은 팀 수와 인원
  const summary = entries.reduce((acc, { change }) => {
    if (change.kind === 'teams') {
      return { ...acc, teams: change.to };
    }
    if (change.kind === 'players') {
      return { ...acc, players: change.to, playersHidden: false };
    }
    if (change.kind === 'playersHidden') {
      return { ...acc, players: null, playersHidden: true };
    }
    return acc;
  }, { teams: null, players: null, playersHidden: false });

  return { entries, summary, wickelineKill, recordWickelineKill };
}
